<template>
  <form class="ui form settings payment-page" @submit.prevent="tokenize">
    <div class="ui grid onboarding-h1-row">
      <div class="sixteen wide column">
        <h1>Payment Details</h1>
      </div>
    </div>

    <div class="ui stackable grid onboarding-content-row payment-content-grid">
      <div class="sixteen wide mobile eight wide computer column payment-form-column">
        <p class="body-large strong payment-intro">
          Review subscription and enter payment details.
        </p>
        <div v-if="hasActiveSubscription" class="ui warning message payment-replace-warning">
          <div class="header">This will replace your current subscription</div>
          <p>
            You already have an active plan. Subscribing will cancel it and replace it with this
            {{ interval === 'yearly' ? 'yearly' : 'monthly' }} plan.
          </p>
        </div>
        <app-stripe-card-fields ref="stripeFields" />
        <div class="field settings payment-referral-field">
          <app-field-label>Referral code (optional)</app-field-label>
          <app-text-input
            :model-value="referralCode"
            placeholder="Enter referral code"
            autocomplete="off"
            @update:model-value="onReferralCodeInput"
          />
        </div>
        <button
          type="submit"
          class="ui fluid primary button settings-save-btn payment-submit-btn"
          :class="{ loading: submitting }"
          :disabled="submitting"
        >
          Subscribe Now
        </button>
        <p class="payment-disclaimer">
          <img src="../../assets/img/icn_lock.svg" alt="" class="payment-disclaimer__icon" aria-hidden="true">
          This is a secure 128-SSL encrypted connection. Payments secured by Stripe.
        </p>
      </div>
      <div class="sixteen wide mobile eight wide computer column payment-summary-column">
        <div class="payment-summary-wrap">
          <router-link to="/onboarding/plan" class="payment-switch-link">Switch pricing plan</router-link>
          <div class="payment-plan-summary">
            <div class="payment-plan-summary__content">
            <div class="payment-plan-summary__main">
              <h3 class="payment-plan-summary__title">{{ interval === 'yearly' ? 'Yearly' : 'Monthly' }}</h3>
              <div v-if="interval === 'yearly'" class="payment-plan-summary__pricing">
                <span class="payment-plan-summary__original">$1020.00</span>
                <span class="payment-plan-summary__price payment-plan-summary__price--accent">$820.00 /year</span>
              </div>
            </div>
            <div class="payment-plan-summary__right">
              <span class="payment-plan-summary__monthly-price">{{ interval === 'yearly' ? '$68.30 /month' : '$85.00 /month' }}</span>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>

    <div class="ui divider onboarding-divider"></div>

    <div class="onboarding-actions">
      <button type="button" class="onboarding-back-btn ui basic button" @click="$router.push('/onboarding/plan')">Back</button>
    </div>
  </form>
</template>

<script>
import axios from 'axios'
import StripeCardFields from '../../components/common/StripeCardFields.vue';
import {
  captureReferralFromQuery,
  getStoredReferralCode,
  setStoredReferralCode,
  clearStoredReferralCode
} from '../../helpers/referral';

export default {
  name: 'OnboardingPayment',
  components: {
    'app-stripe-card-fields': StripeCardFields
  },
  data() {
    return {
      interval: 'yearly',
      submitting: false,
      referralCode: '',
      hasActiveSubscription: false
    };
  },
  mounted() {
    const q = this.$route.query.interval || 'yearly';
    if (q !== 'monthly' && q !== 'yearly') {
      this.$router.replace({ path: '/onboarding/plan' });
      return;
    }
    this.interval = q;
    captureReferralFromQuery(this.$route.query);
    this.referralCode = getStoredReferralCode();
    axios.get('/subscription/status').then((response) => {
      this.hasActiveSubscription = !!(response.data && response.data.subscription_active);
    }).catch(() => {
      this.hasActiveSubscription = false;
    });
  },
  methods: {
    onReferralCodeInput(value) {
      this.referralCode = setStoredReferralCode(value);
    },
    confirmReplaceIfNeeded() {
      if (!this.hasActiveSubscription) return Promise.resolve(true);
      return this.swal({
        title: 'Replace current subscription?',
        text: 'You already have an active plan. Continuing will cancel it and replace it with this ' +
          (this.interval === 'yearly' ? 'yearly' : 'monthly') +
          ' plan. You will be charged for the new plan according to its billing cycle.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Replace and subscribe',
        cancelButtonText: 'Cancel'
      }).then((result) => !!(result && result.value));
    },
    tokenize() {
      const fields = this.$refs.stripeFields;
      if (!fields || !fields.isReady()) {
        this.errorNotification('Payment form is not ready. Please wait a moment.');
        return;
      }
      this.confirmReplaceIfNeeded().then((ok) => {
        if (!ok) return;
        this.submitting = true;
        const code = (this.referralCode || getStoredReferralCode() || '').trim();
        const payload = { token: null, interval: this.interval };
        if (code) payload.referral_code = code;
        fields.createToken().then((token) => {
          payload.token = token.id;
          return axios.post('/subscription', payload);
        }).then(() => {
          clearStoredReferralCode();
          this.successNotification('Subscription updated successfully.');
          this.$router.push('/onboarding/complete');
        }).catch((err) => {
          let msg = 'Payment failed. Please try again.';
          if (err && err.response) {
            msg = this.getSubscriptionErrorMessage(err);
          } else if (err && err.message) {
            msg = err.message;
          }
          this.errorNotification(msg);
        }).finally(() => {
          this.submitting = false;
        });
      });
    },
    getSubscriptionErrorMessage(err) {
      if (!err.response) {
        return 'We couldn\'t reach the server. Check your internet connection and try again. If you\'re on a deployed site, the API may be unavailable or misconfigured.';
      }
      const data = err.response.data;
      if (data && typeof data === 'object') {
        const e = data.error;
        if (typeof e === 'string') return e;
        if (e && typeof e.message === 'string') return e.message;
        if (typeof data.message === 'string') return data.message;
      }
      if (err.response.status === 400) return 'Invalid request. Please check your details and try again.';
      if (err.response.status >= 500) return 'The server is temporarily unavailable. Please try again later.';
      return err.message || 'Payment failed. Please try again.';
    }
  }
};
</script>

<style scoped>
.payment-page {
  padding-bottom: 1rem;
}
.onboarding-h1-row {
  margin-bottom: 0;
}
.onboarding-content-row {
  margin-top: 1rem;
}
/* Wrapper so we can push right column content down to align with Card Number input */
.payment-summary-wrap {
  width: 100%;
}
@media (min-width: 768px) {
  .payment-summary-wrap {
    margin-top: 34px;
  }
  .payment-switch-link {
    display: block;
    text-align: right;
    margin-bottom: 8px;
  }
}
@media (max-width: 767px) {
  .payment-switch-link {
    display: block;
    text-align: right;
    margin-bottom: 8px;
  }
}
.payment-submit-btn {
  margin-bottom: 0 !important;
}
.payment-referral-field {
  margin-top: 1rem;
  margin-bottom: 1rem;
}
.payment-disclaimer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 24px;
  font-size: 0.9rem;
  color: #707070;
  line-height: 1.4;
}
.payment-disclaimer__icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
.payment-summary-column {
  display: flex;
  flex-direction: column;
  align-items: stretch;
}
.payment-switch-link {
  color: #4DA1FF;
  font-size: 0.95rem;
}
/* Static summary card: white background, grey light border (per Figma). 8px gap from link at >=768px via margin-top in media query */
.payment-plan-summary {
  width: 100%;
  min-height: 96px;
  padding: 1rem 1.25rem;
  background: #fff;
  border: 1px solid #CED0DA;
  border-radius: 8px;
}
.payment-plan-summary__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 88px;
}
.payment-plan-summary__main {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.25rem;
}
.payment-plan-summary__title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #323C47;
  font-family: var(--font-regular);
}
.payment-plan-summary__pricing {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.35rem;
}
.payment-plan-summary__original {
  font-size: 0.95rem;
  color: #707070;
  text-decoration: line-through;
}
.payment-plan-summary__price {
  font-size: 0.95rem;
  font-weight: 600;
  color: #323C47;
}
.payment-plan-summary__price--accent {
  color: #4DA1FF;
}
.payment-plan-summary__right {
  flex-shrink: 0;
}
.payment-plan-summary__monthly-price {
  font-size: 0.95rem;
  font-weight: 600;
  color: #323C47;
}
.onboarding-divider {
  margin: 2rem 0 0 0;
  border-top: 1px solid rgba(34, 36, 38, 0.15);
}
.onboarding-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding-top: 0;
}
.onboarding-actions .onboarding-back-btn {
  height: 36px;
  line-height: 36px;
  padding-top: 0;
  padding-bottom: 0;
}
@media (max-width: 767px) {
  .onboarding-actions .onboarding-back-btn {
    width: 100%;
  }
}
</style>
